import {
  isNull,
  isUndefined,
  isDate,
  isNumber,
  isString,
  toNumber,
  set,
} from 'lodash-es';
import debug from 'debug';
import numeral from 'numeral';
import {
  format,
  formatDistanceToNowStrict,
  isValid,
  parse,
  parseJSON,
  parseISO,
  formatISO,
  getWeek,
} from 'date-fns';
import * as dateFnsLocaleHU from 'date-fns/locale/hu';
import {
  GettextTranslate,
  gettextFilters,
  parseGettextExpression,
} from '@integro/gettext';
import type { GetTextTranslations } from '@integro/gettext';
import { LocaleSpec } from './schemas';
import { Application } from './index';

numeral.register('locale', 'hu', {
  delimiters: { thousands: ' ', decimal: ',' },
  abbreviations: { thousand: 'E', million: 'M', billion: 'Mrd', trillion: 'T' },
  ordinal: () => '.',
  currency: { symbol: ' Ft' },
});

const log = debug('itp:core:i18n');

export interface DateTimeFormatOption {
  distanceToNow?: boolean;
}

export interface NumberFormatOptions {
  decimals?: number;
  minDecimals?: number;
}

export interface I18nFilters {
  date(value: any, opts?: DateTimeFormatOption): string;
  datetime(value: any, opts?: DateTimeFormatOption): string;
  time(value: any, opts?: DateTimeFormatOption): string;
  number(value: any, opts?: NumberFormatOptions): string;
  money(value: any, currency: string, digits?: number): string;
  distance(date: Date): string;
}

export class I18n {
  private readonly app?: Application;
  private readonly $$supportedLocales: LocaleSpec[];
  private $$storageKey: string;
  readonly $$translator: GettextTranslate;
  private $$locale: string;
  readonly filters: I18nFilters;

  constructor(args: {
    app?: Application;
    storageKey: string;
    translations: GetTextTranslations[];
    supportedLocales: LocaleSpec[];
  }) {
    this.app = args.app;
    this.$$storageKey = args.storageKey;
    this.$$supportedLocales = args.supportedLocales;
    this.$$translator = new GettextTranslate({
      translations: args.translations,
      defaultLanguage: 'en',
    });
    this.filters = {
      date: this.formatDate.bind(this),
      datetime: this.formatDatetime.bind(this),
      time: this.formatTime.bind(this),
      number: this.formatNumber.bind(this),
      money: this.formatMoney.bind(this),
      distance: this.formatDistance.bind(this),
    };
    this.$$locale = this.loadLocaleCode();
    if (this.app) {
      this.app.events.emit('i18n:locale', this.locale);
    }
  }

  private loadLocaleCode() {
    const lc = getUserLC(this.$$supportedLocales, this.localeCodeStorageKey);
    this.$$translator.language = lc;
    log('set numeral locale to %s', lc);
    numeral.locale(lc);
    return lc;
  }

  get gettextTranslate() {
    return this.$$translator;
  }

  public getLocales() {
    return this.$$supportedLocales;
  }

  public setLocale(lang: string) {
    if (this.$$locale !== lang) {
      log('set locale to "%s"', lang);
      this.$$locale = lang;
      setUserLC(this.localeCodeStorageKey, lang);
      location.reload();
    }
  }

  private readonly $$numberFormatters = new Map<string, Intl.NumberFormat>();

  private getNumberFormatter(
    cacheKey: string,
    options: Dictionary<any>,
  ): Intl.NumberFormat {
    const key = `${this.locale}|${cacheKey}`;
    let formatter = this.$$numberFormatters.get(key);
    if (!formatter) {
      formatter = Intl.NumberFormat(this.locale, options as any);
      this.$$numberFormatters.set(key, formatter);
    }
    return formatter;
  }

  get localeCodeStorageKey() {
    return `${this.$$storageKey}.lc`;
  }

  get locale() {
    return this.$$locale;
  }

  public get LCID() {
    const lang = this.locale;
    return `${lang}-${lang.toUpperCase()}`;
  }

  get dateFormat() {
    return this.locale === 'hu' ? 'YYYY.MM.DD' : 'YYYY-MM-DD';
  }

  get dateFnsDateFormat() {
    /**
     * https://date-fns.org/v2.30.0/docs/format
     */
    return this.locale === 'hu' ? 'yyyy.MM.dd' : 'yyyy-MM-dd';
  }

  translate(
    msgid: string,
    options?: {
      context?: string;
      conversion?: string | string[];
      plural?: string;
      n?: number;
      args?: Dictionary<any>;
    },
  ) {
    return this.$$translator.translate({ msgid, ...options });
  }

  _(msgid: string) {
    return this.translate(msgid);
  }

  translateExpression(expression: string) {
    const parsed = parseGettextExpression(expression);
    if (!parsed.valid) {
      return expression;
    }
    const n = toNumber(parsed.n);
    return this.translate(parsed.msgid, {
      context: parsed.context,
      plural: parsed.plural,
      n: isNaN(n) ? undefined : n,
    });
  }

  parseDate(value?: string | number): Date | undefined {
    if (isNull(value) || isUndefined(value)) {
      return;
    }
    if (isDate(value)) {
      return value;
    }

    let parsed;

    if (isNumber(value)) {
      parsed = parse(value.toString(), 't', new Date());
    }

    if (isString(value)) {
      parsed = parseISO(value);

      if (!isValid(parsed)) {
        parsed = parseJSON(value);
      }
    }

    if (isValid(parsed)) {
      return parsed;
    }
  }

  formatDate(value: any, opts?: DateTimeFormatOption) {
    const d = this.parseDate(value);

    if (isUndefined(d)) {
      return '';
    }

    if (opts && opts.distanceToNow) {
      return this.formatDistance(d);
    }

    return format(d, this.dateFnsDateFormat);
    // return d.toLocaleDateString(this.locale);
  }

  stringifyDate(date?: Date) {
    return date ? formatISO(date) : null;
  }

  formatDatetime(value: any, opts?: DateTimeFormatOption) {
    const d = this.parseDate(value);

    if (isUndefined(d)) {
      return '';
    }

    if (opts && opts.distanceToNow) {
      return this.formatDistance(d);
    }

    return format(d, `${this.dateFnsDateFormat} HH:mm`);
  }

  formatTime(value: any, opts?: DateTimeFormatOption) {
    if (isString(value) && value.length === 5) {
      // hack
      return value;
    }
    if (isString(value) && value.length === 8) {
      // hack
      return value;
    }

    const d = this.parseDate(value);

    if (isUndefined(d)) {
      return '';
    }

    if (opts && opts.distanceToNow) {
      return this.formatDistance(d);
    }

    return d.toLocaleTimeString(this.locale, {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  formatNumber(value: any, opts?: NumberFormatOptions) {
    if (isNull(value) || isUndefined(value)) {
      return '';
    }

    value = toNumber(value);

    if (isNumber(value)) {
      const options: Dictionary<string | boolean> = { useGrouping: true };

      if (opts && opts.decimals) {
        set(options, 'minimumFractionDigits', opts.decimals);
        set(options, 'maximumFractionDigits', opts.decimals);
      }
      if (opts && opts.minDecimals) {
        set(options, 'minimumFractionDigits', opts.minDecimals);
      }

      if (
        isUndefined(options.maximumFractionDigits) ||
        isNull(options.maximumFractionDigits)
      ) {
        set(options, 'maximumFractionDigits', 20);
      }

      return this.getNumberFormatter(
        `n|${opts?.decimals}|${opts?.minDecimals}`,
        options,
      ).format(value);
    }

    return value;
  }

  formatMoney(value: any, currency: string, digits?: number) {
    if (isNull(value) || isUndefined(value)) {
      return '';
    }

    if (isNumber(value)) {
      const options: Dictionary<string | number> = {
        style: 'currency',
        currency,
      };

      if (digits != null) {
        set(options, 'minimumFractionDigits', digits);
        set(options, 'maximumFractionDigits', digits);
      }

      return this.getNumberFormatter(
        `m|${currency}|${digits}`,
        options,
      ).format(value);
    }

    if (isString(value)) {
      console.log('Value is not a number!', value);
    }

    return value;
  }

  formatDistance(date: Date) {
    if (this.locale === 'hu') {
      return formatDistanceToNowStrict(date, {
        locale: dateFnsLocaleHU.default,
        addSuffix: true,
      });
    }

    return formatDistanceToNowStrict(date, {
      addSuffix: true,
    });
  }

  getWeek(date?: Date) {
    return date ? getWeek(date) : null;
  }
}

function setUserLC(storageKey: string, localeCode: string) {
  const $html = document.querySelector('html');
  if ($html) {
    $html.setAttribute('lang', localeCode);
  }
  localStorage.setItem(storageKey, localeCode);
}

function getBrowserLocale() {
  const navLang =
    navigator.languages !== undefined
      ? navigator.languages[0]
      : navigator.language;

  if (!navLang) {
    return;
  }

  return navLang.trim().split(/-|_/)[0];
}

function getUserLC(supportedLocales: LocaleSpec[], storageKey?: string) {
  const all = supportedLocales.map((p) => p.code),
    valid = (code: string) => !!code && all.indexOf(code) > -1;

  let lc = storageKey ? localStorage.getItem(storageKey) : null;

  if (lc && valid(lc)) {
    return lc;
  }

  let lcb = getBrowserLocale();

  if (lcb && valid(lcb)) {
    return lcb;
  }

  return getFallbackLC(supportedLocales);
}

function getFallbackLC(supportedLocales: LocaleSpec[]) {
  for (const locale of supportedLocales) {
    if (locale.fallback) {
      return locale.code;
    }
  }
  return 'en';
}
